/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Thursday, March 3rd 2022, 12:52:31 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import { FIELD_TYPES } from "lib/schemas";
import EmailIcon from "@mui/icons-material/Email";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import SwitchLeftIcon from "@mui/icons-material/SwitchLeft";
import LinkIcon from "@mui/icons-material/Link";
import MapIcon from "@mui/icons-material/Map";
import TextFieldsIcon from "@mui/icons-material/TextFields";
import PhotoCamera from "@mui/icons-material/PhotoCamera";
import ContactPhoneIcon from "@mui/icons-material/ContactPhone";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import TitleIcon from "@mui/icons-material/Title";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import { PenTool as FPenToolIcon } from "react-feather";
import CheckBoxOutlinedIcon from "@mui/icons-material/CheckBoxOutlined";
import LibraryAddCheckOutlinedIcon from "@mui/icons-material/LibraryAddCheckOutlined";
import ListOutlinedIcon from "@mui/icons-material/ListOutlined";
import RadioButtonCheckedOutlinedIcon from "@mui/icons-material/RadioButtonCheckedOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import LanguageOutlinedIcon from "@mui/icons-material/LanguageOutlined";
import SmartButtonOutlinedIcon from "@mui/icons-material/SmartButtonOutlined";
import TableViewOutlinedIcon from "@mui/icons-material/TableViewOutlined";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import AutorenewIcon from "@mui/icons-material/Autorenew";

// Using es6 Map so the original insertion order of the widget info can be preserved which is not the case
// for a standard javascript object that has numerical keys. https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Map
const FIELD_TYPE_WIDGETS: Map<FIELD_TYPES, [string, JSX.Element]> = new Map([
  [FIELD_TYPES.TEXT, ["Single Line Text", <TextFieldsIcon className="os-single-line-text-field-icon" />]],
  [FIELD_TYPES.TEXTAREA, ["Text Area", <TextFieldsIcon className="os-textarea-field-icon" />]],
  [FIELD_TYPES.EMAIL, ["Email", <EmailIcon className="os-email-field-icon" />]],
  [FIELD_TYPES.CHECKBOX, ["CheckBox", <CheckBoxOutlinedIcon className="os-checkbox-field-icon" />]],
  [
    FIELD_TYPES.CHECKBOX_MULTIPLE,
    ["CheckBox Multiple", <LibraryAddCheckOutlinedIcon className="os-multi-checkbox-field-icon" />],
  ],
  [FIELD_TYPES.SELECT, ["Drop Down Selection", <ListOutlinedIcon className="os-dropdown-field-icon" />]],
  [
    FIELD_TYPES.SELECT_MULTIPLE,
    ["Multiple Drop Down Selection", <ListOutlinedIcon className="os-multi-dropdown-field-icon" />],
  ],
  [
    FIELD_TYPES.RADIO_MULTIPLE,
    ["Radio Button Selection", <RadioButtonCheckedOutlinedIcon className="os-radio-field-icon" />],
  ],
  [FIELD_TYPES.FILE, ["File Upload", <UploadFileIcon className="os-file-field-icon" />]],
  [FIELD_TYPES.MULTIPLE_FILE, ["Multiple File Upload", <UploadFileIcon className="os-multi-file-field-icon" />]],
  [FIELD_TYPES.DATE, ["Date Picker", <CalendarTodayIcon className="os-date-field-icon" />]],
  [FIELD_TYPES.DATE_TIME, ["Date/Time Picker", <ScheduleOutlinedIcon className="os-datetime-field-icon" />]],
  [FIELD_TYPES.DOB, ["Date of Birth", <CalendarTodayIcon className="os-dob-field-icon" />]],
  [FIELD_TYPES.HIDDEN, ["Hidden", <VisibilityOffIcon className="os-hidden-field-icon" />]],
  [FIELD_TYPES.NUMBER, ["Number", <SwitchLeftIcon className="os-number-field-icon" />]],
  [FIELD_TYPES.URL, ["URL", <LinkIcon className="os-url-field-icon" />]],
  [FIELD_TYPES.SECTION_BREAK, ["Section", <FormatListBulletedIcon className="os-section-break-field-icon" />]],
  [FIELD_TYPES.GEO_COORDINATES, ["GEO Coordinates", <MapIcon className="os-gis-field-icon" />]],
  [FIELD_TYPES.PAGE_TITLE, ["Page", <MenuBookIcon className="os-page-break-field-icon" />]],
  [FIELD_TYPES.BUTTON_CHECK, ["Button Check", <SmartButtonOutlinedIcon className="os-button-check-field-icon" />]],
  [FIELD_TYPES.MATRIX, ["Matrix", <TableViewOutlinedIcon className="os-matrix-field-icon" />]],
  [FIELD_TYPES.MATRIX_VIEW, ["Matrix View", <TableViewOutlinedIcon className="os-matrix-view-field-icon" />]],
  [FIELD_TYPES.DYNAMIC_MATRIX, ["Dynamic Matrix", <TableViewOutlinedIcon className="os-dynamic-matrix-field-icon" />]],
  [
    FIELD_TYPES.FORM_ENTRIES,
    ["Form Submissions Selection", <ListOutlinedIcon className="os-form-entries-field-icon" />],
  ],
  [
    FIELD_TYPES.MULTI_FORM_ENTRIES,
    ["Multiple Form Submissions Selection", <ListOutlinedIcon className="os-multi-form-entries-field-icon" />],
  ],
  [FIELD_TYPES.TITLE, ["Title", <TitleIcon className="os-title-field-icon" />]],
  [FIELD_TYPES.AUTO_COMPLETE, ["Auto Complete", <ListOutlinedIcon className="os-auto-complete-field-icon" />]],
  [
    FIELD_TYPES.SOURCE_COMPLETE,
    ["Source Complete", <LanguageOutlinedIcon className="os-source-complete-field-icon" />],
  ],
  [FIELD_TYPES.SIGNATURE_PAD, ["Signature Pad", <FPenToolIcon className="os-signature-field-icon" />]],
  [FIELD_TYPES.IMAGE, ["Photo", <PhotoCamera className="os-photo-field-icon" />]],
  [FIELD_TYPES.PHONE_NUMBER, ["Phone", <ContactPhoneIcon className="os-phone-field-icon" />]],
  [
    FIELD_TYPES.AUTO_GENERATED_SEQUENCE,
    ["Auto Generated Sequence", <AutorenewIcon className="os-auto-sequence-field-icon" />],
  ],
]);

export const getFieldTypeWidgets = (fieldType: FIELD_TYPES) => FIELD_TYPE_WIDGETS.get(fieldType) ?? [null, null, null];

// Excluding these until the configuration is availble on this admin console. When we want to add a new
// field available for configuration, remove it from this list.
const EXCLUDED_FIELD_TYPES = [
  FIELD_TYPES.AUTO_COMPLETE,
  FIELD_TYPES.BUTTON_CHECK,
  FIELD_TYPES.ERP_RECORD,
  FIELD_TYPES.MATRIX,
  FIELD_TYPES.MATRIX_VIEW,
  FIELD_TYPES.SOURCE_COMPLETE,
];

export const getConfigurableFieldTypes = () => {
  const configurableFieldTypes = [];
  for (const field_type of FIELD_TYPE_WIDGETS.keys()) {
    if (!EXCLUDED_FIELD_TYPES.includes(field_type)) configurableFieldTypes.push(field_type);
  }
  return configurableFieldTypes;
};
